import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Select, MenuItem, FormControl, InputLabel,
  Card, CardContent, Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { PageHeader, StatusBadge, EmptyState, useFeedback } from '../components/ui';
import { color } from '../theme/tokens';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning', icon: '⏳' },
  accepted: { label: 'Accepted', color: 'info', icon: '✓' },
  picked_up: { label: 'Picked Up', color: 'primary', icon: '📦' },
  out_for_delivery: { label: 'Out for Delivery', color: 'secondary', icon: '🚚' },
  delivered: { label: 'Delivered', color: 'success', icon: '✅' },
  cancelled: { label: 'Cancelled', color: 'error', icon: '✗' },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const FastDelivery = () => {
  const { admin } = useAuth();
  const { success, error } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [deliveryUsers, setDeliveryUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  
  const isDeliveryUser = admin?.usertype === 'delivery';
  const isAdmin = admin?.usertype === 'superadmin' || admin?.usertype === 'admin';

  const fetchRequests = useCallback(async (status = null) => {
    setLoading(true);
    try {
      const endpoint = isDeliveryUser 
        ? '/api/delivery/my-requests'
        : '/api/delivery/admin/board';
      
      const params = status && status !== 'all' ? { status } : {};
      const res = await api.get(endpoint, { params });
      setRequests(res.data.requests || []);
    } catch (err) {
      console.error('Error fetching delivery requests:', err);
      error('Failed to fetch delivery requests');
    } finally {
      setLoading(false);
    }
  }, [isDeliveryUser, error]);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/api/delivery/admin/stats');
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [isAdmin]);

  const fetchDeliveryUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/api/delivery/admin/users');
      setDeliveryUsers(res.data.users || []);
    } catch (error) {
      console.error('Error fetching delivery users:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRequests(currentTab === 'all' ? null : currentTab);
    fetchStats();
    fetchDeliveryUsers();

    const interval = setInterval(() => {
      fetchRequests(currentTab === 'all' ? null : currentTab);
      fetchStats();
    }, 8000);
    return () => clearInterval(interval);
  }, [currentTab, fetchRequests, fetchStats, fetchDeliveryUsers]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleAccept = async (requestId) => {
    try {
      await api.post(`/api/delivery/accept/${requestId}`);
      success('Delivery request accepted');
      fetchRequests(currentTab === 'all' ? null : currentTab);
    } catch (err) {
      error(err.response?.data?.error || 'Failed to accept request');
    }
  };

  const handleUpdateStatus = async (requestId, newStatus, note = '') => {
    try {
      await api.put(`/api/delivery/${requestId}/status`, { status: newStatus, note });
      success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchRequests(currentTab === 'all' ? null : currentTab);
    } catch (err) {
      error(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleAssignUser = async () => {
    if (!selectedRequest || !selectedUser) return;
    
    const user = deliveryUsers.find(u => u.id === parseInt(selectedUser));
    if (!user) return;

    try {
      await api.post(`/api/delivery/admin/${selectedRequest.request_id}/assign`, {
        userId: user.id,
        userName: user.username,
      });
      success(`Assigned to ${user.username}`);
      setAssignDialogOpen(false);
      setSelectedUser('');
      fetchRequests(currentTab === 'all' ? null : currentTab);
    } catch (err) {
      error(err.response?.data?.error || 'Failed to assign delivery user');
    }
  };

  const openAssignDialog = (request) => {
    setSelectedRequest(request);
    setSelectedUser(request.assigned_delivery_user_id || '');
    setAssignDialogOpen(true);
  };

  const getNextStatus = (currentStatus) => {
    const transitions = {
      pending: 'accepted',
      accepted: 'picked_up',
      picked_up: 'out_for_delivery',
      out_for_delivery: 'delivered',
    };
    return transitions[currentStatus];
  };

  const filteredRequests = requests;

  return (
    <Box>
      <PageHeader
        title={isDeliveryUser ? 'My Deliveries' : 'Delivery Management'}
        subtitle={isDeliveryUser 
          ? 'Manage your assigned delivery requests'
          : 'Overview and management of all delivery requests'}
        action={
          <IconButton onClick={() => fetchRequests(currentTab === 'all' ? null : currentTab)} size="small">
            <RefreshIcon />
          </IconButton>
        }
      />

      {isAdmin && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">In Progress</Typography>
                <Typography variant="h4">{stats.in_progress || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
                <Typography variant="h4">{stats.pending || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Delivered</Typography>
                <Typography variant="h4" color="success.main">{stats.delivered || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Total (30d)</Typography>
                <Typography variant="h4">{stats.total_requests || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="All" value="all" />
          <Tab label="Pending" value="pending" />
          <Tab label="Accepted" value="accepted" />
          <Tab label="Picked Up" value="picked_up" />
          <Tab label="Out for Delivery" value="out_for_delivery" />
          <Tab label="Delivered" value="delivered" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredRequests.length === 0 ? (
        <EmptyState
          icon={<ShippingIcon sx={{ fontSize: 64, color: color.ink[300] }} />}
          title="No delivery requests"
          message="No delivery requests found for the selected status"
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Store</TableCell>
                {isAdmin && <TableCell>Assigned To</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((req) => (
                <TableRow key={req.request_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      #{req.order_id}
                    </Typography>
                    {req.item_count && (
                      <Typography variant="caption" color="text.secondary">
                        {req.item_count} items
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{req.customer_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {req.mobile || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {req.delivery_address || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PIN: {req.delivery_pin || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{req.store_name || 'Unassigned'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {req.store_city || '—'}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {req.assigned_delivery_user_name ? (
                        <Chip 
                          size="small" 
                          label={req.assigned_delivery_user_name}
                          icon={<PersonIcon />}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <StatusBadge 
                      label={STATUS_CONFIG[req.status]?.label || req.status}
                      color={STATUS_CONFIG[req.status]?.color || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(req.created_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {/* Accept Button */}
                      {(isDeliveryUser || isAdmin) && req.status === 'pending' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          startIcon={<CheckIcon fontSize="small" />}
                          onClick={() => handleAccept(req.request_id)}
                          sx={{ textTransform: 'none', py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                        >
                          Accept
                        </Button>
                      )}
                      
                      {/* Advance Stage Button */}
                      {(isDeliveryUser || isAdmin) && req.status !== 'pending' && req.status !== 'delivered' && req.status !== 'cancelled' && (
                        <Button
                          size="small"
                          variant="contained"
                          color={req.status === 'out_for_delivery' ? 'success' : 'primary'}
                          startIcon={<ShippingIcon fontSize="small" />}
                          onClick={() => handleUpdateStatus(req.request_id, getNextStatus(req.status))}
                          sx={{ textTransform: 'none', py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                        >
                          {STATUS_CONFIG[getNextStatus(req.status)]?.label || 'Next'}
                        </Button>
                      )}
                      
                      {/* Assign Rider Dialog Button */}
                      {isAdmin && (
                        <Tooltip title="Assign or Change Delivery Rider">
                          <IconButton 
                            size="small" 
                            onClick={() => openAssignDialog(req)}
                            sx={{ border: `1px solid ${color.divider}` }}
                          >
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign User Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Delivery User</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Delivery User</InputLabel>
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="Delivery User"
            >
              {deliveryUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username} {user.active_requests > 0 && `(${user.active_requests} active)`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained" disabled={!selectedUser}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FastDelivery;
